import { Check, Copy } from 'lucide-react'
import Prism from 'prismjs'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-c'
import 'prismjs/components/prism-cpp'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import Markdown, { type Components } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { copyToClipboard } from '@/lib/copy-to-clipboard'
import { cn } from '@/lib/utils'

/**
 * Copy button for code blocks. Extracted so it can use hooks (useState).
 * Accepts either a static string or a getter function for lazy text extraction.
 */
function CodeCopyButton({
  code,
  getCode,
}: {
  code?: string
  getCode?: () => string
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const text = code ?? getCode?.() ?? ''
    try {
      await copyToClipboard(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'absolute right-2 top-2 z-10 rounded-md p-1.5',
        'bg-muted/80 hover:bg-muted border border-border/50',
        REVEAL_ON_HOVER,
        copied && 'text-emerald-500',
      )}
      title={copied ? 'Copied!' : 'Copy Code'}
      aria-label={copied ? 'Copied!' : 'Copy code'}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  )
}

/**
 * A code block that applies Prism syntax highlighting after mount.
 * Used as a custom component for react-markdown's `code` element.
 */
function HighlightedCode({
  className,
  children,
}: {
  className?: string
  children?: ReactNode
}) {
  const codeRef = useRef<HTMLElement>(null)

  // Extract language from className (e.g., "language-python" -> "python")
  const match = /language-(\w+)/.exec(className || '')
  const language = match ? match[1] : ''

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-highlight when content or language changes
  useEffect(() => {
    if (codeRef.current && language) {
      Prism.highlightElement(codeRef.current)
    }
  }, [children, language])

  return (
    <code ref={codeRef} className={className}>
      {children}
    </code>
  )
}

/**
 * Wrapper for fenced code blocks (pre element) that adds a copy button.
 * react-markdown renders fenced code as <pre><code class="language-*">...</code></pre>
 */
function CodeBlockPre({ children, ...props }: React.ComponentProps<'pre'>) {
  const preRef = useRef<HTMLPreElement>(null)

  // Extract text content for copy from the code element inside
  const getCodeText = () => preRef.current?.textContent ?? ''

  return (
    <div className="group relative">
      <CodeCopyButton getCode={getCodeText} />
      <pre ref={preRef} {...props}>
        {children}
      </pre>
    </div>
  )
}

// Custom components for react-markdown
const markdownComponents: Components = {
  a({ children, ...props }) {
    return (
      <a target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    )
  },
  // Wrap fenced code blocks (<pre>) with copy button
  pre({ children, ...props }) {
    return <CodeBlockPre {...props}>{children}</CodeBlockPre>
  },
  code({ className, children, ...props }) {
    // Check if this is a fenced code block (has language class) vs inline code
    const isInlineCode = !className?.startsWith('language-')

    if (isInlineCode) {
      // Render inline code normally
      return (
        <code className={className} {...props}>
          {children}
        </code>
      )
    }

    // Render fenced code blocks with syntax highlighting
    return <HighlightedCode className={className}>{children}</HighlightedCode>
  },
}

interface SyntaxHighlightedMarkdownProps {
  children: string
  className?: string
  components?: Components
}

/**
 * A markdown renderer that applies Prism syntax highlighting to fenced code blocks.
 * Use this anywhere you want code blocks in markdown to be highlighted.
 *
 * Accepts an optional `components` prop to override or extend the default
 * component mappings (e.g., custom h2/h3 renderers for editorial approach headings).
 *
 * Example:
 * ```tsx
 * <SyntaxHighlightedMarkdown>
 *   {`Here is some code:
 *
 * \`\`\`python
 * def hello():
 *     print("Hello!")
 * \`\`\`
 * `}
 * </SyntaxHighlightedMarkdown>
 * ```
 */
export function SyntaxHighlightedMarkdown({
  children,
  className,
  components,
}: SyntaxHighlightedMarkdownProps) {
  const mergedComponents = components
    ? { ...markdownComponents, ...components }
    : markdownComponents

  return (
    <article
      className={
        className ??
        'prose prose-sm dark:prose-invert max-w-none prose-headings:text-base prose-headings:font-medium prose-headings:tracking-tight prose-p:leading-relaxed'
      }
    >
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={mergedComponents}
      >
        {children}
      </Markdown>
    </article>
  )
}
